import { useEffect, useState } from "react";
import { Copy, KeyRound, Mail, MessageCircle, Send } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import PageHero from "@/components/bakery/PageHero";
import { ChartArt } from "@/components/bakery/Art";
import { Field, inputCls } from "@/components/common/Field";
import { Card, Loading, OutlineBtn, PrimaryBtn } from "@/components/common/ui";
import { Tag } from "@/components/common/Chips";
import { useApi } from "@/lib/useApi";
import { API, api, errMsg } from "@/lib/api";
import { fmtDateTime, shiftDate, todayJkt } from "@/lib/format";

const TIMES = Array.from({ length: 96 }, (_, i) => `${String(Math.floor(i / 4)).padStart(2, "0")}:${String((i % 4) * 15).padStart(2, "0")}`);
const lines = (s) => s.split(/[\n,]/).map((x) => x.trim()).filter(Boolean);

function TestSend() {
  const [date, setDate] = useState(shiftDate(todayJkt(), -1));
  const [ch, setCh] = useState({ email: true, whatsapp: false });
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/settings/report/test", { date, channels: Object.keys(ch).filter((k) => ch[k]), app_url: window.location.origin });
      setRes(data.results);
      data.results.forEach((r) => (r.status === "success" ? toast.success(`${r.channel}: terkirim`) : toast.error(`${r.channel}: ${r.error}`)));
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Card className="border-2 border-ink" data-testid="test-send-card">
      <div className="mb-3 font-display text-lg font-bold">Kirim Tes Sekarang</div>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
        <Field label="Tanggal laporan"><input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inputCls} data-testid="test-send-date" /></Field>
        <label className="flex items-center gap-2 text-sm"><Switch checked={ch.email} onCheckedChange={(v) => setCh({ ...ch, email: v })} data-testid="test-send-email" /> Email</label>
        <label className="flex items-center gap-2 text-sm"><Switch checked={ch.whatsapp} onCheckedChange={(v) => setCh({ ...ch, whatsapp: v })} data-testid="test-send-wa" /> WhatsApp</label>
        <PrimaryBtn onClick={run} disabled={busy || (!ch.email && !ch.whatsapp)} data-testid="test-send-btn"><Send className="h-4 w-4" /> {busy ? "Mengirim…" : "Kirim Tes"}</PrimaryBtn>
      </div>
      {res && <ul className="mt-3 space-y-1 text-sm" data-testid="test-send-results">{res.map((r) => <li key={r.channel}><b>{r.channel}</b>: {r.status === "success" ? "terkirim" : <span className="text-brand">gagal — {r.error}</span>}</li>)}</ul>}
    </Card>
  );
}

function ApiKeyCard({ apiKey, onNew }) {
  const gen = async () => {
    if (apiKey && !window.confirm("Buat API key baru? Key lama langsung tidak berlaku.")) return;
    try { const { data } = await api.post("/settings/api-key"); onNew(data.api_key); toast.success("API key dibuat"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Card data-testid="api-key-card">
      <div className="mb-1 flex items-center gap-2 font-display text-lg font-bold"><KeyRound className="h-5 w-5" /> Report API (read-only)</div>
      <p className="mb-3 text-sm text-zinc-600">GET <code className="num rounded bg-zinc-100 px-1.5 text-xs">{API}/reports/daily?date=YYYY-MM-DD</code> dengan header <code className="num rounded bg-zinc-100 px-1.5 text-xs">X-API-Key</code>.</p>
      <div className="flex flex-col gap-2 sm:flex-row">
        <input readOnly value={apiKey || "Belum ada API key"} className={`${inputCls} num text-xs`} data-testid="api-key-value" />
        {apiKey && <OutlineBtn className="h-12" onClick={() => { navigator.clipboard?.writeText(apiKey); toast.success("Disalin"); }} data-testid="api-key-copy"><Copy className="h-4 w-4" /></OutlineBtn>}
        <OutlineBtn className="h-12" onClick={gen} data-testid="api-key-generate">{apiKey ? "Buat Ulang" : "Buat API Key"}</OutlineBtn>
      </div>
    </Card>
  );
}

function Logs() {
  const { data } = useApi("/settings/report-logs");
  return (
    <Card className="overflow-hidden p-0" data-testid="report-logs">
      <div className="border-b-2 border-ink px-5 py-3 font-display text-lg font-bold">Log Pengiriman</div>
      <div className="max-h-80 overflow-auto">
        <table className="dense-table w-full">
          <thead><tr><th>Waktu</th><th>Laporan</th><th>Kanal</th><th>Jenis</th><th>Status</th><th>Percobaan</th><th>Error</th></tr></thead>
          <tbody>{(data || []).map((l, i) => (
            <tr key={i}><td>{fmtDateTime(l.created_at)}</td><td>{l.date}</td><td>{l.channel}</td><td>{l.kind}</td>
              <td>{l.status === "success" ? <Tag tone="soft">sukses</Tag> : <Tag tone={l.status === "failed" ? "red" : "ink"}>{l.status}</Tag>}</td>
              <td className="num">{l.attempts}</td><td className="max-w-[260px] truncate" title={l.error}>{l.error}</td></tr>
          ))}</tbody>
        </table>
        {!data?.length && <p className="p-5 text-sm text-zinc-500">Belum ada pengiriman.</p>}
      </div>
    </Card>
  );
}

export default function Pengaturan() {
  const { data, loading, setData } = useApi("/settings/report");
  const [f, setF] = useState(null);
  const [token, setToken] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (data) setF({ ...data, emails: data.email_recipients.join("\n"), phones: data.wa_recipients.join("\n") }); }, [data]);
  if (loading || !f) return <Loading />;
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v?.target ? v.target.value : v }));
  const save = async () => {
    setSaving(true);
    try {
      const { data: d } = await api.put("/settings/report", { send_time: f.send_time, email_enabled: f.email_enabled, email_recipients: lines(f.emails), wa_enabled: f.wa_enabled,
        wa_provider: f.wa_provider, fonnte_token: token || null, wa_recipients: lines(f.phones), app_url: window.location.origin });
      setData(d); setToken(""); toast.success("Pengaturan tersimpan");
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  return (
    <div data-testid="pengaturan-page">
      <PageHero testid="settings-hero" eyebrow="Pengaturan · Laporan Harian" title="Laporan Harian Otomatis" Art={ChartArt} compact
        desc="Setiap hari sistem mengirim laporan H-1 (zona Asia/Jakarta) lewat email dan WhatsApp sesuai jam di bawah." />
      <div className="grid gap-6 xl:grid-cols-2">
        <Card className="space-y-5" data-testid="report-settings-card">
          <Field label="Jam kirim (WIB)" hint="Dicek otomatis tiap 15 menit; dikirim sekali per hari setelah jam ini">
            <select value={f.send_time} onChange={set("send_time")} className={inputCls} data-testid="settings-send-time">{TIMES.map((t) => <option key={t}>{t}</option>)}</select>
          </Field>
          <div className="rounded-lg border border-zinc-200 p-4">
            <label className="flex items-center gap-2 font-semibold"><Mail className="h-4 w-4" /> Email <Switch className="ml-auto" checked={f.email_enabled} onCheckedChange={set("email_enabled")} data-testid="settings-email-enabled" /></label>
            <p className="mt-1 text-xs text-zinc-500">Subjek: [Stronghold Daily Report] YYYY-MM-DD · lampiran Excel Kartu Stok Ringkasan Harian</p>
            <Field label="Penerima (satu per baris)" className="mt-3"><Textarea value={f.emails} onChange={set("emails")} data-testid="settings-email-recipients" /></Field>
          </div>
          <div className="rounded-lg border border-zinc-200 p-4">
            <label className="flex items-center gap-2 font-semibold"><MessageCircle className="h-4 w-4" /> WhatsApp <Switch className="ml-auto" checked={f.wa_enabled} onCheckedChange={set("wa_enabled")} data-testid="settings-wa-enabled" /></label>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <Field label="Provider"><select value={f.wa_provider} onChange={set("wa_provider")} className={inputCls} data-testid="settings-wa-provider"><option value="fonnte">Fonnte</option><option value="meta">WhatsApp Cloud API (Meta) — segera</option></select></Field>
              <Field label="Token Fonnte" hint={f.fonnte_token_set ? `Tersimpan ${f.fonnte_token_hint} · isi untuk mengganti` : "Belum diisi"}>
                <input type="password" value={token} onChange={(e) => setToken(e.target.value)} placeholder="Tempel token" className={inputCls} data-testid="settings-fonnte-token" autoComplete="off" />
              </Field>
            </div>
            <Field label="Nomor WA penerima (satu per baris, mis. 0812…)" className="mt-3"><Textarea value={f.phones} onChange={set("phones")} data-testid="settings-wa-recipients" /></Field>
          </div>
          <PrimaryBtn className="w-full" onClick={save} disabled={saving} data-testid="settings-save">{saving ? "Menyimpan…" : "Simpan Pengaturan"}</PrimaryBtn>
        </Card>
        <div className="space-y-6">
          <TestSend />
          <ApiKeyCard apiKey={f.api_key} onNew={(k) => setF((x) => ({ ...x, api_key: k }))} />
        </div>
      </div>
      <div className="mt-6"><Logs /></div>
    </div>
  );
}
